import { plainToClass } from "class-transformer";
import { ActionItemListingDto, SiteLevelSummaryDto, SaveActionItemDto } from "./action-items.objects";
import { IFilter } from "../../../shared/scripts/filtering/filtering.entities";
import { Unique } from "../../../shared/scripts/array-functions";
import { InitializeFilterDropdown } from "../../../shared/scripts/filtering/filtering.utilities";
import { ActionItemDialog, ActionItemDialogReadonly } from "./components/action-item.dialog";
import { AuditHistoryDialog } from "../audit/scripts/audit-history.dialog";

declare global {
    interface Window {
        page: any;
    }
}

export class ActionItemsPage implements IFilter {
    actionItems: ActionItemListingDto[];
    filterDropdownIds: string[];
    dataTable: any;
    datePickerId: string = '#due-date-field';
    datePickerId2: string = '#due-date-field-2';
    datePickerConditional: string = '.due-date-conditional';
    dateOperatorId: string = '#due-date-operator';
    actionItemDialog: ActionItemDialog | ActionItemDialogReadonly;
    isNonViewer: boolean;

    constructor(allActionItemsJson: any, allActionItemTypesJson: any, allActionItemStatusesJson: any, allActionItemCriticalTypesJson: any, allSitesJson: any, allUsersJson: any, isNonViewer: boolean) {
        $(() => {
            this.actionItems = plainToClass(ActionItemListingDto, <ActionItemListingDto[]>allActionItemsJson);
            this.filterDropdownIds = [
                '#site-dropdown', '#status-dropdown', '#type-dropdown',
                '#critical-dropdown', '#person-dropdown'
            ];
            this.isNonViewer = isNonViewer === true;
            this.initializeFilters();
            this.initializeTable();
            $(this.datePickerConditional).css("visibility","hidden");

            // Instantiate dialog logic and pass required data
            const auditDialog = new AuditHistoryDialog();
            if (this.isNonViewer) {
                this.actionItemDialog = new ActionItemDialog(
                    allActionItemsJson,
                    allActionItemTypesJson,
                    allActionItemStatusesJson,
                    allActionItemCriticalTypesJson,
                    allSitesJson,
                    allUsersJson,
                    auditDialog
                );
                this.actionItemDialog.successCallback = this.saveSuccess.bind(this);
            } else {
                this.actionItemDialog = new ActionItemDialogReadonly(
                    allActionItemsJson,
                    allActionItemTypesJson,
                    allActionItemStatusesJson,
                    allActionItemCriticalTypesJson,
                    allSitesJson,
                    allUsersJson,
                    auditDialog
                );
            }
        });
    }

    initializeFilters(): void {
        // Site filter
        let siteValues: string[] = this.actionItems.map(ai => ai.SiteName);
        InitializeFilterDropdown('#site-dropdown', Unique(siteValues, true, ["None Selected"]), this);
        // Status filter
        let statusValues: string[] = this.actionItems.map(ai => ai.Status);
        InitializeFilterDropdown('#status-dropdown', Unique(statusValues, true, ["None Selected"]), this);
        // Type filter
        let typeValues: string[] = this.actionItems.map(ai => ai.Type);
        InitializeFilterDropdown('#type-dropdown', Unique(typeValues, true, ["None Selected"]), this);
        // Critical filter
        let criticalValues: string[] = this.actionItems.map(ai => ai.IsCritical ? "Critical" : "Not Critical");
        InitializeFilterDropdown('#critical-dropdown', Unique(criticalValues, true, ["None Selected"]), this);
        // Person Responsible filter
        let personValues: string[] = [];
        this.actionItems.forEach(ai => personValues.push(...ai.UserNames));
        personValues.push("Unassigned");
        InitializeFilterDropdown('#person-dropdown', Unique(personValues, true, ["None Selected"]), this);
        // Due Date filter
        const self = this;
        $(this.dateOperatorId).on('change', function () {
            const op = $(self.dateOperatorId).val();
            if (op === 'between') {
                $(self.datePickerConditional).css("visibility", "visible");
            } else {
                $(self.datePickerConditional).css("visibility", "hidden");
            }
            self.triggerFilter();
        });
        $(this.datePickerId + ',' + this.datePickerId2).on('change input', () => {
            this.triggerFilter();
        });

        // Set min/max for datepickers
        $(this.datePickerId).on('change', function () {
            let startDate = "";
            let dateCheck = $(self.datePickerId).val();
            if (Array.isArray(dateCheck)) {
                startDate = dateCheck.length > 0 ? String(dateCheck[0]) : "";
            } else {
                startDate = String(dateCheck);
            }
            if (startDate) {
                $(self.datePickerId2).attr('min', startDate);
            } else {
                $(self.datePickerId2).removeAttr('min');
            }
        });
        $(this.datePickerId2).on('change', function () {
            let endDate = "";
            let dateCheck = $(self.datePickerId2).val();
            if (Array.isArray(dateCheck)) {
                endDate = dateCheck.length > 0 ? String(dateCheck[0]) : "";
            } else {
                endDate = String(dateCheck);
            }
            if (endDate) {
                $(self.datePickerId).attr('max', endDate);
            } else {
                $(self.datePickerId).removeAttr('max');
            }
        });
    }

    initializeTable(): void {
        this.dataTable = $('#action-items-table').DataTable({
            dom: 'rtip',
            autoWidth: false,
            info: false,
            paging: false,
            search: true,
            scrollX: true,
            scrollY: '60vh',
            scrollCollapse: true,
            orderCellsTop: true,
            order: [[3, 'asc']], // Order by Due Date
            columns: [
                { data: "Type", title: "Type" },
                { data: "Description", title: "Description"},
                { data: "Status", title: "Status" },
                { data: "SiteName", title: "Site" },
                { data: "DateDue", title: "Due Date" },
                { data: "IsUrgent", title: "Urgency", render: (data) => data ? "Urgent" : "" },
                { data: "IsCritical", title: "Critical", render: (data) => data ? "Critical" : "" },
                {
                    data: null,
                    title: "",
                    className: "ctms-table-col-actions",
                    orderable: false,
                    render: function () {
                        return '<button><i class="ctms-icon-pencil"></i></button>';
                    }
                }
            ],
            data: this.actionItems
        });

        $('#custom-dt-search').on('input', function () {
            $('#action-items-table').DataTable().search(String($(this).val() ?? '')).draw();
        });
        $('#custom-dt-search-clear').on('click', function () {
            $('#custom-dt-search').val('');
            $('#action-items-table').DataTable().search('').draw();
        });

        $(document).on('click', '#action-items-table .ctms-icon-pencil', (evt) => {
            var row = $(evt.target).closest('tr');
            var data = $('#action-items-table').DataTable().row(row).data();
            if (!data || !data.Id) return;
            if (this.isNonViewer) {
                $('#newActionItemDialogLabel').text('Edit Action Item');
            }
            else {
                $('#newActionItemDialogLabel').text('View Action Item');
            }
            $.get('/secure/ctms/action-items/action-items?handler=ActionItemDto&id=' + data.Id, (response) => {
                if (response && response.success && response.actionItem) {
                    if (this.actionItemDialog) {
                        $('#newActionItemDialog').modal('show');
                        this.actionItemDialog.populateDialogFieldsFromDto(response.actionItem);
                    }
                } else {
                    alert('Could not load action item for editing.');
                }
            });
        });

        $('#newActionItemBtn').on('click', function () {
            $('#newActionItemDialogLabel').text('New Action Item');
            $('#actionItemId').val(0);
        });
    }

    updateTable(matchingItems: ActionItemListingDto[]): void {
        this.dataTable.clear();
        this.dataTable.rows.add(matchingItems).draw();
    }

    triggerFilter(): void {
        const selectedSite = this.getSelectedDropdownValues('#site-dropdown');
        const selectedStatus = this.getSelectedDropdownValues('#status-dropdown');
        const selectedType = this.getSelectedDropdownValues('#type-dropdown');
        const selectedCritical = this.getSelectedDropdownValues('#critical-dropdown');
        const selectedPerson = this.getSelectedDropdownValues('#person-dropdown');
        let dateCheck = $(this.dateOperatorId).val();
        let dateOperator = "";
        let date1 = "";
        let date2 = "";
        if (Array.isArray(dateCheck)) {
            dateOperator = dateCheck.length > 0 ? String(dateCheck[0]) : "";
        } else {
            dateOperator = String(dateCheck);
        }
        dateCheck = $(this.datePickerId).val();
        if (Array.isArray(dateCheck)) {
            date1 = dateCheck.length > 0 ? String(dateCheck[0]) : "";
        } else {
            date1 = String(dateCheck);
        }
        dateCheck = $(this.datePickerId2).val();
        if (Array.isArray(dateCheck)) {
            date2 = dateCheck.length > 0 ? String(dateCheck[0]) : "";
        } else {
            date2 = String(dateCheck);
        }
        

        const matchingActionItems: ActionItemListingDto[] = [];

        this.actionItems.forEach(item => {
            if (item.matchesFilter(
                selectedSite,
                selectedStatus,
                selectedType,
                selectedCritical,
                selectedPerson,
                { operator: dateOperator, date1: date1, date2: date2 }
            )) {
                matchingActionItems.push(item);
            }
        });

        this.updateTable(matchingActionItems);
    }

    getSelectedDropdownValues(selectId: string): any {
        return $(selectId).val();
    }

    resetFilters(): void {
        this.filterDropdownIds.forEach((dropdownId: string) => {
            $(dropdownId).selectpicker('selectAll');
            $(dropdownId).selectpicker('refresh');
        });
        $(this.dateOperatorId).val('on');
        $(this.datePickerId).val('').trigger('change');
        $(this.datePickerId2).val('').trigger('change');
        $(this.datePickerConditional).css("visibility", "hidden");
        $('#custom-dt-search').val('');
        this.dataTable.search('').draw();
        this.updateTable(this.actionItems);
    }

    saveSuccess(response:any) {
        if (response && response.success && response.actionItems) {
            const pascalCaseActionItems = mapKeysToPascalCase(response.actionItems);
            this.actionItems = plainToClass(ActionItemListingDto, <ActionItemListingDto[]>pascalCaseActionItems);
            this.filterDropdownIds.forEach(id => {
                $(id).selectpicker('destroy');
                $(id).empty();
            });
            this.resetFilters();
            this.updateTable(this.actionItems);
            this.initializeFilters();

        }
    }
}

function mapKeysToPascalCase(obj: any): any {
    if (Array.isArray(obj)) {
        return obj.map(mapKeysToPascalCase);
    }
    if (obj !== null && typeof obj === 'object') {
        const newObj: any = {};
        for (const key in obj) {
            if (obj.hasOwnProperty(key)) {
                const pascalKey = key.charAt(0).toUpperCase() + key.slice(1);
                newObj[pascalKey] = mapKeysToPascalCase(obj[key]);
            }
        }
        return newObj;
    }
    return obj;
}